import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Loader2, AlertTriangle, Rocket, DollarSign, CalendarClock } from 'lucide-react';
import { adminMetricsApi, type GrowthMetrics } from '../../../api/adminMetricsApi';
import CountUp from '../../../components/ui/CountUp';

const SEG_COLORS: Record<string, string> = {
  'Pro': '#8b5cf6',
  'Activo (gratis)': '#16a34a',
  'Enfriándose': '#f59e0b',
  'Abandonó': '#f97316',
  'Nunca arrancó': '#64748b',
};
const SEG_ORDER = ['Pro', 'Activo (gratis)', 'Enfriándose', 'Abandonó', 'Nunca arrancó'];

const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const recencia = (d: number | null) => (d == null ? '—' : d === 0 ? 'hoy' : `${d}d`);
// Antigüedad legible: meses si pasa de ~2 meses, días si no.
const antig = (d: number | null) => (d == null ? '—' : d >= 60 ? `${Math.round(d / 30)} meses` : `${d}d`);

/**
 * Panel de Crecimiento (admin): cataloga los NEGOCIOS por uso real (recencia),
 * no por visitas históricas. Muestra embudo/catálogo, altas por mes y las
 * listas accionables: candidatos a Pro, a recuperar y a activar.
 * "Analítica Accionable" aplicada al panel admin.
 */
const BusinessGrowthPanel: React.FC = () => {
  const [data, setData] = useState<GrowthMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await adminMetricsApi.growth();
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'Error cargando crecimiento');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="bg-surface shadow rounded-lg p-6 border border-line flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin h-5 w-5 text-indigo-500 mr-3" />
        <span className="text-content-3 text-sm">Calculando crecimiento de negocios...</span>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg p-6 flex items-center gap-3">
        <AlertTriangle className="h-5 w-5 text-red-500" />
        <span className="text-sm text-red-700 dark:text-red-300">{error || 'Sin datos de crecimiento.'}</span>
      </div>
    );
  }

  const d = data;
  const dark = isDark();
  const nunca = d.seg['Nunca arrancó'] || 0;
  const aband = d.seg['Abandonó'] || 0;
  const top = d.candidatosPro[0];
  const segMax = Math.max(...SEG_ORDER.map((k) => d.seg[k] || 0), 1);

  const kpis = [
    { v: d.total, l: 'Negocios registrados', sub: '', color: 'var(--c-content)' },
    { v: d.activosAhora, l: 'Usándolo ahora', sub: `${Math.round(d.activosAhora / d.total * 100)}% del total`, color: '#16a34a' },
    { v: `${d.conversionPro}%`, l: 'Conversión a Pro', sub: `${d.seg['Pro']} pagan`, color: '#8b5cf6' },
    { v: nunca, l: 'Nunca arrancaron', sub: `${Math.round(nunca / d.total * 100)}% · el gran reto`, color: '#dc2626' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Rocket className="h-6 w-6 text-indigo-500" />
        <div>
          <h2 className="text-xl font-bold text-content">Panel de Crecimiento</h2>
          <p className="text-sm text-content-2">Catálogo y conversión de los negocios que usan Lealify</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => (
          <div key={i} className="bg-surface shadow rounded-lg p-4 border border-line">
            <div className="text-3xl font-display font-extrabold leading-none" style={{ color: k.color }}>{typeof k.v === 'number' ? <CountUp value={k.v} /> : k.v}</div>
            <div className="text-xs text-content-3 mt-1.5">{k.l}</div>
            {k.sub && <div className="text-xs text-content-3">{k.sub}</div>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Catálogo */}
        <div className="bg-surface shadow rounded-lg p-6 border border-line">
          <h3 className="text-sm font-medium text-content-2 mb-4">Catálogo de negocios (por uso real)</h3>
          <div className="space-y-2.5">
            {SEG_ORDER.map((k) => {
              const v = d.seg[k] || 0;
              const w = Math.max(4, (v / segMax) * 100);
              return (
                <div key={k} className="flex items-center gap-3 text-sm">
                  <div className="w-32 text-content-2">{k}</div>
                  <div className="flex-1 h-5 rounded bg-surface-2 overflow-hidden">
                    <div className="h-full rounded flex items-center px-2 text-white text-xs font-semibold"
                      style={{ width: `${w}%`, background: SEG_COLORS[k] }}>{v}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="bg-surface-2 rounded-lg px-3.5 py-2.5 text-[13px] text-content-2 mt-4">
            <b>{d.activosAhora}</b> negocios usan Lealify hoy · <b>{aband}</b> lo usaban y se fueron · <b>{nunca}</b> casi no lo usaron.
          </div>
        </div>

        {/* Altas + insight */}
        <div className="bg-surface shadow rounded-lg p-6 border border-line">
          <h3 className="text-sm font-medium text-content-2 mb-3">Altas de negocios por mes</h3>
          <ResponsiveContainer width="100%" height={150}>
            <AreaChart data={d.altas} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="altasGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#334155' : '#e5e7eb'} vertical={false} />
              <XAxis dataKey="mes" tickFormatter={(m: string) => m.slice(2)} tick={{ fontSize: 10, fill: dark ? '#94a3b8' : '#6b7280' }} tickLine={false} minTickGap={16} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: dark ? '#94a3b8' : '#6b7280' }} tickLine={false} axisLine={false} width={28} />
              <Tooltip contentStyle={{ background: dark ? '#1e293b' : '#fff', border: `1px solid ${dark ? '#334155' : '#e5e7eb'}`, borderRadius: 8, fontSize: 12 }} formatter={(v: any) => [`${v} altas`, '']} />
              <Area type="monotone" dataKey="n" stroke="#6366f1" strokeWidth={2} fill="url(#altasGrad)" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="border-l-4 border-amber-400 pl-3 mt-3">
            <p className="text-[13px] text-content-2 leading-relaxed">
              Vende Pro a los <b>{d.candidatosPro.length} activos gratis</b>{top ? <> (empezando por <b>{top.name}</b>, {top.vis.toLocaleString('es-MX')} visitas)</> : ''}.
              Recupera a los <b>{aband}</b> que se fueron. De los <b>{nunca}</b> que no arrancaron, enfócate en los recién registrados.
            </p>
          </div>
        </div>
      </div>

      {/* Ingresos */}
      {d.revenue && (
        <div className="bg-surface shadow rounded-lg p-6 border border-line" style={{ borderTop: '3px solid #16a34a' }}>
          <h3 className="text-sm font-medium text-content-2 mb-4 flex items-center">
            <DollarSign className="h-4 w-4 mr-1.5 text-green-500" /> Ingresos por suscripciones
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
            <div><div className="text-2xl font-display font-extrabold text-green-600 dark:text-green-500">$<CountUp value={d.revenue.arr} /></div><div className="text-xs text-content-3">Ingreso anual (ARR)</div></div>
            <div><div className="text-2xl font-display font-extrabold text-content">$<CountUp value={d.revenue.mrr} /></div><div className="text-xs text-content-3">Mensual (MRR)</div></div>
            <div><div className="text-2xl font-display font-extrabold text-content"><CountUp value={d.revenue.pagando} /></div><div className="text-xs text-content-3">{d.revenue.planPagando.pro} Pro · {d.revenue.planPagando.premium} Premium</div></div>
            <div><div className="text-2xl font-display font-extrabold text-amber-500"><CountUp value={d.revenue.enPrueba.length} /></div><div className="text-xs text-content-3">en prueba (no cuentan)</div></div>
          </div>
          {d.revenue.enPrueba.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg px-3.5 py-2.5 text-[12px] text-amber-800 dark:text-amber-300 mb-4">
              En prueba (excluidos del ingreso): {d.revenue.enPrueba.map(p => p.name).join(', ')}. {d.revenue.propios > 0 ? `Además ${d.revenue.propios} negocio propio excluido.` : ''}
            </div>
          )}
          <h4 className="text-xs font-medium text-content-2 mb-2 flex items-center"><CalendarClock className="h-3.5 w-3.5 mr-1.5 text-content-3" /> Próximas renovaciones (cobros a asegurar)</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-content-3 text-[11px] uppercase">
                <th className="text-left font-semibold py-1.5 px-2">Negocio</th>
                <th className="text-left font-semibold py-1.5 px-2">Plan</th>
                <th className="text-right font-semibold py-1.5 px-2">Monto</th>
                <th className="text-right font-semibold py-1.5 px-2">Activo</th>
                <th className="text-right font-semibold py-1.5 px-2">Vence</th>
              </tr></thead>
              <tbody>
                {d.revenue.renovaciones.map((r) => {
                  const urgente = r.venceDias != null && r.venceDias <= 30;
                  return (
                    <tr key={r.id} className="border-t border-line">
                      <td className="py-2 px-2 text-content font-medium">{r.name}</td>
                      <td className="py-2 px-2 text-content-2 capitalize">{r.plan}</td>
                      <td className="py-2 px-2 text-right text-content-2">${r.precio.toLocaleString('es-MX')}</td>
                      <td className="py-2 px-2 text-right text-content-2">{r.antigMeses != null ? `${r.antigMeses}m` : '—'}</td>
                      <td className={`py-2 px-2 text-right font-medium ${urgente ? 'text-red-600 dark:text-red-400' : 'text-content-2'}`}>{r.venceDias != null ? `${r.venceDias}d` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Clientes Pro en riesgo de dormirse (protección de ingreso) */}
      {d.revenue?.proEnRiesgo && d.revenue.proEnRiesgo.length > 0 && (
        <GrowthList title="⚠️ Clientes Pro en riesgo de dormirse" subtitle="Pagan pero dejaron de usar Lealify. Ordenados por inactividad: los de arriba son proclives a no renovar." accent="#dc2626"
          head={['Negocio', 'Plan', 'Últ. visita', 'Últ. cliente nuevo', 'Vence en']}
          rows={d.revenue.proEnRiesgo.map((c) => [c.name, c.plan, c.ultVisita == null ? 'nunca' : recencia(c.ultVisita), recencia(c.ultReg ?? null), c.venceDias != null ? `${c.venceDias}d` : '—'])}
          empty="Todos tus clientes Pro están activos. 👍" />
      )}

      {/* Listas accionables */}
      <GrowthList title="🔥 Candidatos a Pro — activos que no pagan" subtitle="Usan Lealify ahora y siguen en gratis. Ordenados por actividad más reciente." accent="#16a34a"
        head={['Negocio', 'Visitas', 'Clientes', 'Últ. visita', 'Últ. cliente nuevo', 'Registrado']}
        rows={d.candidatosPro.map((c) => [c.name, c.vis.toLocaleString('es-MX'), String(c.cli), recencia(c.dias), recencia(c.ultReg), antig(c.altaDias)])} empty="Sin candidatos activos." />

      <GrowthList title="↩️ Recuperar — usaban Lealify y se fueron" subtitle="Tuvieron uso real pero abandonaron. Campaña de regreso." accent="#f59e0b"
        head={['Negocio', 'Visitas (histórico)', 'Clientes', 'Sin venir']}
        rows={d.recuperar.map((c) => [c.name, c.vis.toLocaleString('es-MX'), String(c.cli), `${c.dias}d`])} empty="Nadie abandonó con uso real. 👍" />

      <GrowthList title="🌱 Activar — registrados que nunca arrancaron (recientes)" subtitle={`De los ${nunca} que no arrancaron, los más recientes valen el intento de onboarding.`} accent="#3b82f6"
        head={['Negocio', 'Días desde alta', 'Onboarding']}
        rows={d.activar.map((c) => [c.name, `${c.diasAlta}d`, c.onb ? '✓ completó' : 'pendiente'])} empty="Sin registros recientes pendientes." />
    </div>
  );
};

const GrowthList: React.FC<{ title: string; subtitle: string; accent: string; head: string[]; rows: string[][]; empty: string }> = ({ title, subtitle, accent, head, rows, empty }) => (
  <div className="bg-surface shadow rounded-lg p-6 border border-line" style={{ borderTop: `3px solid ${accent}` }}>
    <h3 className="text-sm font-medium text-content-2">{title}</h3>
    <p className="text-[12px] text-content-3 mb-3">{subtitle}</p>
    {rows.length === 0 ? (
      <p className="text-sm text-content-3 py-4 text-center">{empty}</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-content-3 text-[11px] uppercase">
              {head.map((h, i) => <th key={i} className={`font-semibold py-1.5 px-2 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={ri} className="border-t border-line">
                {r.map((cell, ci) => <td key={ci} className={`py-2 px-2 ${ci === 0 ? 'text-content font-medium' : 'text-right text-content-2'}`}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default BusinessGrowthPanel;
